import { Story } from '../../types/story';

export const barcelonaNewYear: Story = {
  id: 'barcelona-new-year',
  title: 'Midnight in Barcelona',
  excerpt: 'New Year\'s Eve on a Barcelona rooftop, where twelve grapes and a stranger lead to an unforgettable night.',
  city: 'Barcelona',
  publishDate: '2025-12-31',
  readTime: '13 min read',
  tags: ['♂♀', 'new-year', 'rooftop', 'celebration'],
  categories: ['forbidden', 'adventurous'],
  intensity: 3,
  content: `I'd come to Barcelona to escape New Year's Eve in London—the forced fun, the overpriced parties, the pressure to have the perfect night. Instead, I'd rented a tiny apartment in El Born and planned to spend midnight alone with a bottle of cava and a book.

But my Airbnb host, Rosa, had other ideas.

"You cannot spend New Year's alone," she insisted when she found me on the building's rooftop terrace at 11 PM. "It's bad luck. Come, we're having a small party. Just neighbors."

The "small party" turned out to be about thirty people, all locals, all speaking rapid Catalan that I couldn't follow. I was about to make my excuses when I saw him.

He was leaning against the railing, looking out over the city, a bottle of beer in his hand. Tall, dark hair, the kind of face that belonged in a Almodóvar film—beautiful and slightly dangerous.

"You look as uncomfortable as I feel," he said in English when I approached the railing.

"That obvious?"

"You're standing alone at a party full of people who've known each other for years. Either you're uncomfortable or you're a sociopath." He smiled. "I'm Marc."

"Emma."

"Let me guess—tourist who thought Barcelona would be romantic on New Year's Eve?"

"Close. Expat who thought Barcelona would be less depressing than London on New Year's Eve."

"And is it?"

"Ask me at midnight."

We talked as the countdown approached, easy conversation that felt like we'd known each other longer than thirty minutes. He was a photographer, originally from Barcelona but living in Berlin, back for the holidays. We compared notes on cities we'd lived in, places we wanted to go, the strange loneliness of being surrounded by celebration when you're alone.

"Five minutes," someone called out.

Rosa appeared with a bowl of grapes. "You know the tradition, yes? Twelve grapes, twelve chimes, twelve wishes for the new year."

"I've heard of it," I said.

"You must do it properly," Rosa insisted. "One grape for each chime. If you finish all twelve, your wishes come true."

Marc took the bowl, counted out twelve grapes for each of us. "No pressure."

The countdown began. The crowd gathered at the railing, looking out over the city where fireworks were already starting to bloom in the distance.

"Diez... nueve... ocho..."

Marc moved closer, close enough that our shoulders touched.

"Siete... seis... cinco..."

I could feel my heart racing, and not from the grapes.

"Cuatro... tres... dos... uno... ¡Feliz Año Nuevo!"

The bells began to chime. We ate our grapes, laughing as we tried to keep up with the rhythm. I finished my twelfth grape just as the final bell rang, and Marc kissed me.

It was supposed to be a New Year's kiss—quick, celebratory, meaningless. But it didn't feel meaningless. It felt like the start of something.

When we broke apart, the rooftop had erupted in celebration. Fireworks lit up the sky, champagne corks popped, people embraced and cheered. In the chaos, Marc took my hand.

"Want to get out of here?"

"Where?"

"I know a place."

We slipped away while everyone was distracted, down the stairs and out into the street. Barcelona was alive with celebration—people spilling out of bars, music from every direction, strangers hugging strangers.

Marc led me through the Gothic Quarter, down narrow streets I'd never have found on my own, until we reached a small plaza with a fountain. It was quieter here, the celebration a distant roar.

"This is my favorite place in Barcelona," he said. "I used to come here as a kid when the city got too loud."

"It's perfect."

He pulled me close, kissed me again, slower this time. His hands were in my hair, my hands under his jacket, both of us pressed against the fountain like teenagers.

"Come back to my place," he murmured against my lips.

"Where?"

"My mother's apartment. She's in Sitges for the holiday. We'd have it to ourselves."

I should have said no. Should have been sensible, safe, smart. But it was New Year's Eve, and I'd made twelve wishes, and he tasted like grapes and possibility.

"Yes."

His mother's apartment was in Gràcia, a top-floor flat with a terrace overlooking the neighborhood. We could still hear the celebration, see fireworks in the distance, but up here it felt like we were alone in the city.

We didn't make it to the bedroom. We barely made it inside.

He pushed me against the door, kissing me with an urgency that matched my own. My jacket hit the floor, then his, then my dress, until we were stumbling toward the couch, leaving a trail of clothes behind us.

"Wait," he said, pulling back. "I need to know—is this just tonight? Because if it is, that's fine. But I need to know."

"I don't know," I admitted. "I wasn't planning on this. Any of this."

"Neither was I." He traced my collarbone with his finger. "But I don't want this to be just tonight."

"You live in Berlin. I live in London."

"I know. But maybe... maybe we figure it out. If we want to."

"Do you want to?"

"Ask me in the morning."

We made love on his mother's couch, then on the floor, then finally in his childhood bedroom, surrounded by posters of bands I'd never heard of and football trophies from another life. It was intense and tender and nothing like the anonymous hookup I'd expected.

Afterward, we lay tangled in his narrow bed, listening to the city slowly quiet down.

"What did you wish for?" I asked. "With the grapes."

"I can't tell you. It won't come true."

"That's birthday candles, not New Year's grapes."

"Same principle." He kissed my shoulder. "What did you wish for?"

"To not be alone."

"That's only one wish. You get twelve."

"I used the other eleven on variations of the same theme."

He laughed, pulled me closer. "Well, for tonight at least, wish granted."

I stayed until morning, watching the sun rise over Barcelona from his terrace. We made coffee, shared a cigarette even though neither of us really smoked, talked about logistics and possibilities and whether long-distance could work.

"I'm coming back to Barcelona in March," he said. "For work. Stay with me. We'll have a week."

"And then?"

"And then we figure out April. And May. And all the rest."

It's been three years now. We still live in different cities—him in Berlin, me in London, both of us too stubborn to give up our lives completely. But we've gotten good at the in-between. Long weekends in Barcelona, holidays in Berlin, summers in London.

And every New Year's Eve, no matter where we are, we eat twelve grapes at midnight. I still can't tell you what I wished for that first year. But I can tell you it came true.

Sometimes the best things happen when you stop planning and just let the night unfold. Sometimes a stranger on a rooftop becomes the person you can't imagine your life without. Sometimes twelve grapes and a kiss at midnight are enough to change everything.

¡Feliz Año Nuevo.`
};
